import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { STATUS_TONES } from './presentation';
import { useBackupDevicesForTicket, useBackupJob, useRestartBackup } from './queries';

/**
 * Der Kasten am Backup-Ticket -- F5-01, Anforderung 3.
 *
 * **Er erscheint nur, wenn es etwas zu zeigen gibt.** Ist das Ticket kein
 * Backup-Ticket, kommt eine leere Liste, und dann steht hier nichts -- kein
 * leerer Kasten mit einer Überschrift. Ein Bereich, der bei neunundneunzig
 * von hundert Tickets leer wäre, ist neunundneunzigmal Platz ohne Aussage.
 *
 * **Warum der Knopf hierhin gehört und nicht nur in die Sicherungsübersicht.**
 * Wer ein Backup-Ticket bearbeitet, ist bereits am richtigen Ort. Ihn für die
 * naheliegendste Handlung -- „nochmal versuchen" -- in einen anderen Bereich
 * zu schicken, wo er das Gerät erst wiederfinden muss, ist ein Umweg, den er
 * täglich geht.
 *
 * **Der Zustand steht daneben, mit der Fehlerzahl.** Ein Ticket sagt, dass
 * etwas gemeldet wurde; erst der Zustand sagt, ob es noch gilt. Ohne ihn
 * würde man eine Sicherung erneut anstoßen, die seit gestern wieder läuft.
 */
export function BackupTicketPanel({
  ticketId,
  canManage,
}: {
  ticketId: string;
  canManage: boolean;
}) {
  const { t, i18n } = useTranslation();

  const devices = useBackupDevicesForTicket(ticketId);
  const restart = useRestartBackup();
  const [watchedJob, setWatchedJob] = useState<string | null>(null);
  const job = useBackupJob(watchedJob);

  if (devices.isPending || devices.isError || devices.data.length === 0) return null;

  return (
    <section className="card">
      <h2>{t('backup.title')}</h2>

      {devices.data.map((device) => (
        <div className="stack" key={device.id}>
          <dl className="definition-list">
            <dt>{t('backup.device')}</dt>
            <dd>{device.computer_name || device.account_name}</dd>

            <dt>{t('backup.status')}</dt>
            <dd>
              <Badge tone={STATUS_TONES[device.last_status]}>
                {t(`backupStatus.${device.last_status}`)}
              </Badge>{' '}
              {t('backup.errorCountValue', { count: device.error_count })}
            </dd>

            <dt>{t('backup.lastSuccess')}</dt>
            <dd>
              {device.last_success_at
                ? t('backup.daysAgo', {
                    count: device.days_since_success ?? 0,
                    date: new Date(device.last_success_at).toLocaleDateString(i18n.language),
                  })
                : t('backup.never')}
            </dd>
          </dl>

          {canManage ? (
            <Button
              variant="secondary"
              disabled={restart.isPending}
              onClick={() => {
                restart.mutate(
                  { deviceId: device.id, ticketId },
                  {
                    onSuccess: (created) => {
                      setWatchedJob(created.id);
                    },
                  },
                );
              }}
            >
              {restart.isPending ? t('backup.restarting') : t('backup.restart')}
            </Button>
          ) : null}
        </div>
      ))}

      {restart.isError ? <ErrorNotice error={restart.error} /> : null}

      {/* Auch ein abgelehnter Auftrag steht hier -- mit der Meldung des
          Anbieters. Ein Knopf, der bei Fehler 13501 nichts sagt, ist
          schlimmer als keiner. */}
      {job.data ? (
        <p>
          <Badge tone={job.data.status === 'failed' ? 'danger' : 'info'}>
            {t(`backupJobStatus.${job.data.status}`, { defaultValue: job.data.status })}
          </Badge>{' '}
          {job.data.error ? <span className="table__muted">{job.data.error}</span> : null}
        </p>
      ) : null}
    </section>
  );
}
